import React, { useCallback, useEffect, useRef, useState } from "react";
import * as activityService from "../../services/activityService";
import { FILE_UPLOADS_ENABLED, UPLOADS_DISABLED_MESSAGE } from "../../config/features";
import { DOCUMENT_CATEGORIES, documentCategoryLabel, documentStatus } from "../../utils/documentUtils";
import styles from "./DocumentsPanel.module.css";

// Documents tab for a person or resource: pick a type, optionally an expiry date, then drop or choose files.
export default function DocumentsPanel({ entityType, entityId, canEdit, onChange }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState(entityType === "resource" ? "service_record" : "contract");
  const [expiresOn, setExpiresOn] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setFiles(await activityService.listFiles(entityType, entityId));
    } catch (err) {
      setError(err.response?.data?.error || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    setError("");
    try {
      for (const file of fileList) {
        await activityService.uploadFile(entityType, entityId, file, { category, expires_on: expiresOn });
      }
      await load();
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (file) => {
    try {
      await activityService.deleteFile(entityType, entityId, file.file_id);
      await load();
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.error || "Could not delete the file.");
    }
  };

  const categories = entityType === "resource"
    ? DOCUMENT_CATEGORIES.filter((c) => c.value !== "medical" && c.value !== "id")
    : DOCUMENT_CATEGORIES;
  const uploadsOn = canEdit && FILE_UPLOADS_ENABLED;

  return (
    <div className={styles.panel}>
      {canEdit && (
        <div className={styles.addRow}>
          <label className={styles.field}>
            <span>Type</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>Expires (optional)</span>
            <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
          </label>
        </div>
      )}

      {canEdit && (
        <div
          className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ""} ${uploadsOn ? "" : styles.dropZoneDisabled}`}
          onDragOver={(e) => { e.preventDefault(); if (uploadsOn) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (uploadsOn) upload(e.dataTransfer.files); }}
          title={FILE_UPLOADS_ENABLED ? undefined : UPLOADS_DISABLED_MESSAGE}
        >
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} />
          {uploading ? "Uploading…" : (
            <>
              Drop files here or{" "}
              <button type="button" className={styles.linkButton} disabled={!uploadsOn} onClick={() => inputRef.current?.click()}>
                choose files
              </button>
            </>
          )}
        </div>
      )}

      {error && <p className={styles.error} role="alert">{error}</p>}

      {loading ? (
        <p className={styles.empty}>Loading documents…</p>
      ) : files.length === 0 ? (
        <p className={styles.empty}>No documents yet.</p>
      ) : (
        <ul className={styles.list}>
          {files.map((file) => {
            const status = documentStatus(file.expires_on);
            return (
              <li key={file.file_id} className={styles.item}>
                <div className={styles.itemMain}>
                  <button
                    type="button"
                    className={styles.fileName}
                    onClick={() => activityService.openAttachment(entityType, entityId, file.file_id).catch(() => setError("Could not open the file."))}
                  >
                    {file.original_name}
                  </button>
                  <span className={styles.meta}>
                    {documentCategoryLabel(file.category)}
                    {file.expires_on && ` · expires ${new Date(file.expires_on).toLocaleDateString()}`}
                  </span>
                </div>
                <span className={`${styles.chip} ${styles[`chip_${status.tone}`]}`}>{status.label}</span>
                {canEdit && (
                  <button type="button" className={styles.remove} onClick={() => remove(file)} aria-label={`Delete ${file.original_name}`}>
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
